import { BitkitAlert, type BitkitAlertProps } from '@bitrise/bitkit-v2';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { differenceInDays, differenceInHours } from 'date-fns';
import { useContext, useMemo } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { segmentTrackOnce } from '@/hooks/analytics/useTracking';
import { useDismissableNotification } from '@/hooks/useDismissableNotification';
import { BuildCacheTrial } from '@/models/Account';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import { getWorkspaceRole } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/utils/getWorkspaceRole';
import NotifyWorkspaceOwnerDialog from '@/pages/BuildCache/components/BuildCacheActivation/NotifyWorkspaceOwnerDialog';
import {
  BuildCacheActivationSourcePageType,
  NotificationCategoryType,
} from '@/pages/BuildCache/components/BuildCacheActivation/types/BuildCacheActivation';
import { buildCachePlanSelectorPagePath } from '@/utils/monolithApiRouteService';

type ActivationAlertProps = Pick<BitkitAlertProps, 'alignSelf'> & {
  sourcePageType: BuildCacheActivationSourcePageType;
  /** The add-on-resolved trial; covers workspaces whose GlobalProps trial mirror is missing. */
  trial?: BuildCacheTrial;
};

const ActivationAlert = ({ sourcePageType, alignSelf = 'self-start', trial: resolvedTrial }: ActivationAlertProps) => {
  const {
    account: {
      slug: accountSlug,
      buildCacheTrial: mirroredTrial,
      hasBuildCacheSubscription: hasSubscription,
      buildCacheSubscriptionRequest: enterpriseSubscriptionRequest,
      buildCacheConsumption,
      canViewWorkspace,
      canActivateBuildCacheSubscription,
      canViewBillingData,
    },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const trial = resolvedTrial ?? mirroredTrial;

  const trialEndsCookie = `build_cache_trial_ends_in_seven_days_notification-${accountSlug}`;
  const quotaCookie = `build-cache-reached-three-quarters-of-limit-${accountSlug}`;

  const canActivateSubscription = canActivateBuildCacheSubscription && canViewBillingData;

  const workspaceRole = useMemo(
    () => getWorkspaceRole({ canViewWorkspace, canViewBillingData }),
    [canViewWorkspace, canViewBillingData],
  );

  const { isHidden: trialDismissed, dismiss: dismissTrial } = useDismissableNotification(trialEndsCookie);
  const { isHidden: quotaDismissed, dismiss: dismissQuota } = useDismissableNotification(quotaCookie);

  const {
    open: isNotifyWorkspaceOwnerDialogOpen,
    onClose: onNotifyWorkspaceOwnerDialogClose,
    onOpen: onNotifyWorkspaceOwnerDialogOpen,
  } = useDisclosure();

  if (!trial && !hasSubscription && !enterpriseSubscriptionRequest) {
    return null;
  }

  let title: string | undefined;
  let message: string | undefined;
  let action: BitkitAlertProps['action'];
  let status: BitkitAlertProps['variant'];
  let onClose: (() => void) | undefined;
  let notificationCategory: NotificationCategoryType | undefined;
  let ctaButtonText: string | undefined;

  if (enterpriseSubscriptionRequest?.requestedAt && !hasSubscription) {
    title = 'Your Build Cache subscription request is pending';
    status = 'info';
    notificationCategory = NotificationCategoryType.subscriptionPending;

    if (canActivateSubscription) {
      message = 'We have received your request and will be in touch soon.';
    } else {
      message = 'Your Build Cache subscription request is pending.';
      title = undefined;
    }
  } else {
    status = 'warning';

    if (hasSubscription) {
      const isOverlimits =
        buildCacheConsumption &&
        buildCacheConsumption.currentInvocationCount > buildCacheConsumption.invocationCountLimit;
      const hasReachedThreeQuartersOfLimit =
        buildCacheConsumption &&
        buildCacheConsumption.currentInvocationCount >= buildCacheConsumption.invocationCountLimit * 0.75;

      if (isOverlimits) {
        title = 'Usage has exceeded plan limits';
        notificationCategory = NotificationCategoryType.quotaUsed;

        if (canActivateSubscription) {
          message = 'To avoid interruptions, consider upgrading your Build Cache plan.';
          action = {
            label: 'Change plan',
            href: buildCachePlanSelectorPagePath(accountSlug, { params: { source: sourcePageType } }),
          };
          ctaButtonText = 'Change plan';
        } else {
          message = 'To continue using Build Cache, notify your workspace owner to upgrade your plan.';

          if (canViewWorkspace) {
            action = { label: 'Notify workspace owner', onClick: onNotifyWorkspaceOwnerDialogOpen };
            ctaButtonText = 'Notify workspace owner';
          }
        }
      } else if (hasReachedThreeQuartersOfLimit) {
        if (quotaDismissed) {
          return null;
        }

        title = 'You have used 75% of your plan quota';
        onClose = dismissQuota;
        notificationCategory = NotificationCategoryType.quotaAlmostUsed;

        if (canActivateSubscription) {
          message = 'To avoid interruptions, consider upgrading your Build Cache plan.';
          action = {
            label: 'Change plan',
            href: buildCachePlanSelectorPagePath(accountSlug, { params: { source: sourcePageType } }),
          };
          ctaButtonText = 'Change plan';
        } else {
          message = 'To avoid interruptions, notify your workspace owner to upgrade your plan.';

          if (canViewWorkspace) {
            action = { label: 'Notify workspace owner', onClick: onNotifyWorkspaceOwnerDialogOpen };
            ctaButtonText = 'Notify workspace owner';
          }
        }
      } else {
        return null;
      }
    } else if (trial) {
      if (trial.isActive) {
        const isTrialEndsInSevenDays = differenceInDays(new Date(trial.expiresAt), new Date()) === 7;
        const isTrialEndsInOneDay = differenceInHours(new Date(trial.expiresAt), new Date()) <= 24;

        if (isTrialEndsInSevenDays) {
          if (trialDismissed) {
            return null;
          }

          title = '7 days left from your Build Cache trial.';
          onClose = dismissTrial;
          notificationCategory = NotificationCategoryType.trialEndsIn7Days;

          if (canActivateSubscription) {
            message = 'Subscribe to a plan to continue reducing build durations.';
            action = {
              label: 'Select plan',
              href: buildCachePlanSelectorPagePath(accountSlug, { params: { source: sourcePageType } }),
            };
            ctaButtonText = 'Select plan';
          } else {
            message = 'To continue reducing build durations, notify your workspace owner to upgrade your plan.';

            if (canViewWorkspace) {
              action = { label: 'Notify workspace owner', onClick: onNotifyWorkspaceOwnerDialogOpen };
              ctaButtonText = 'Notify workspace owner';
            }
          }
        } else if (isTrialEndsInOneDay) {
          title = 'Less than a day left from your Build Cache trial.';
          notificationCategory = NotificationCategoryType.trialEndsIn24Hours;

          if (canActivateSubscription) {
            message = 'Subscribe to a plan to continue reducing build durations.';
            action = {
              label: 'Select plan',
              href: buildCachePlanSelectorPagePath(accountSlug, { params: { source: sourcePageType } }),
            };
            ctaButtonText = 'Select plan';
          } else {
            message = 'To continue reducing build durations, notify your workspace owner to upgrade your plan.';

            if (canViewWorkspace) {
              action = { label: 'Notify workspace owner', onClick: onNotifyWorkspaceOwnerDialogOpen };
              ctaButtonText = 'Notify workspace owner';
            }
          }
        } else {
          return null;
        }
      } else {
        title = 'Build Cache trial has ended';
        notificationCategory = NotificationCategoryType.trialEnded;

        if (canActivateSubscription) {
          message = 'Subscribe to a plan to continue reducing build durations.';
          action = {
            label: 'Select plan',
            href: buildCachePlanSelectorPagePath(accountSlug, { params: { source: sourcePageType } }),
          };
          ctaButtonText = 'Select plan';
        } else {
          message = 'To continue reducing build durations, notify your workspace owner to upgrade your plan.';

          if (canViewWorkspace) {
            action = { label: 'Notify workspace owner', onClick: onNotifyWorkspaceOwnerDialogOpen };
            ctaButtonText = 'Notify workspace owner';
          }
        }
      }
    }
  }

  segmentTrackOnce('Build Cache Subscription Notification Displayed', {
    cta_button_text: ctaButtonText,
    notification_category: notificationCategory,
    source: sourcePageType,
    workspace_slug: accountSlug,
    workspace_role: workspaceRole,
  });

  return (
    <>
      <BitkitAlert
        variant={status}
        action={action}
        onClose={onClose}
        dismissible={!!onClose}
        titleText={title}
        messageText={message}
        alignSelf={alignSelf}
        data-testid="activationNotification"
        data-clarity-unmask="true"
      />
      <NotifyWorkspaceOwnerDialog
        isOpen={isNotifyWorkspaceOwnerDialogOpen}
        onClose={onNotifyWorkspaceOwnerDialogClose}
      />
    </>
  );
};

export default ActivationAlert;
